import { useOptimisticMutation } from '@noor/lib';
import {
  promotionApi,
  promotionKeys,
  type Promotion,
  type PromotionFilters,
  type PromotionListScope,
  type Promotions,
} from '@/entities/promotion';

/**
 * Delete ONE promotion row — the product × branch pair the merchant clicked —
 * optimistically dropping just that row from the cached list; the helper
 * restores it if the request fails.
 *
 * Scoped to the row on purpose. A promotion covers a cross product of
 * products × branches and the list returns one joined row per pair, all
 * sharing the promotion id — so `DELETE /promotions/{id}`, which retires the
 * whole CAMPAIGN, would take every sibling row down with the one that was
 * clicked. `DELETE /promotions/{id}/products` unlinks the named product at the
 * named branch instead, leaving the campaign running everywhere else, which is
 * what a per-row delete should mean. (`restaurant_id` is what keeps it to one
 * branch: without it the product comes off every restaurant sharing the
 * promotion.)
 *
 * The optimistic patch matches by {@link promotionRowKey} — id alone is the
 * campaign, shared across siblings, so filtering by it would blank rows the
 * request never touched.
 *
 * Takes the whole ROW rather than a bare id because `useOptimisticMutation`
 * passes its variables to the cache patch, and the toast needs the product
 * name for its confirmation copy.
 *
 * `filters` must be the ones the SCREEN is currently showing: the list is
 * keyed by them, so patching the unfiltered key would leave the visible
 * (filtered) rows untouched and the deleted row on screen until a refetch.
 *
 * The result says whether the unlink APPLIED or was QUEUED behind moderation
 * (`pending: true`). A queued unlink leaves the row alive server-side, so the
 * settle-time refetch brings it back — the caller surfaces that as "awaiting
 * review" rather than letting the reappearing row read as a failed delete.
 */
export function useDeletePromotion(
  filters: PromotionFilters = {},
  scope: PromotionListScope = { branches: [] },
) {
  return useOptimisticMutation<{ pending: boolean }, Promotion, Promotions>({
    meta: { suppressErrorToast: true },
    queryKey: promotionKeys.list(filters, scope),
    mutationFn: (promotion) => promotionApi.remove(promotion.id),
    update: (data, promotion) => {
      return {
        ...data,
        items: data.items.filter((item) => String(item.id) !== String(promotion.id)),
      };
    },
  });
}
